import range from 'lodash/range';

const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;
const MINUTE_STEP = 5;
const TIME_PART_LENGTH = 2;
const TIME_DIGITS_LENGTH = 4;
const TIME_SEPARATOR = ':';
const START_OF_TIME_PART = '00';
const COMPLETE_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

interface TimeParts {
  hour: string;
  minute: string;
}

const padTimePart = (value: number): string => String(value).padStart(TIME_PART_LENGTH, '0');

export const HOUR_OPTIONS: string[] = range(HOURS_IN_DAY).map(padTimePart);

export const MINUTE_OPTIONS: string[] = range(0, MINUTES_IN_HOUR, MINUTE_STEP).map(padTimePart);

export const isCompleteTime = (time: string): boolean => COMPLETE_TIME_PATTERN.test(time);

const keepDigits = (value: string): string => value.replace(/\D/g, '');

export const addTimeColon = (inputValue: string): string => {
  const separatorIndex = inputValue.indexOf(TIME_SEPARATOR);
  if (separatorIndex >= 0) {
    const hours = keepDigits(inputValue.slice(0, separatorIndex)).slice(0, TIME_PART_LENGTH);
    const minutes = keepDigits(inputValue.slice(separatorIndex + TIME_SEPARATOR.length)).slice(0, TIME_PART_LENGTH);
    return `${hours}${TIME_SEPARATOR}${minutes}`;
  }
  const digits = keepDigits(inputValue).slice(0, TIME_DIGITS_LENGTH);
  if (digits.length <= TIME_PART_LENGTH) {
    return digits;
  }
  return `${digits.slice(0, TIME_PART_LENGTH)}${TIME_SEPARATOR}${digits.slice(TIME_PART_LENGTH)}`;
};

export const getNextOptionIndex = (key: string, currentIndex: number, optionCount: number): number | undefined => {
  const lastIndex = optionCount - 1;
  switch (key) {
    case 'ArrowDown':
      return Math.min(currentIndex + 1, lastIndex);
    case 'ArrowUp':
      return Math.max(currentIndex - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return lastIndex;
    default:
      return undefined;
  }
};

export const getTimeParts = (time: string): TimeParts | undefined => {
  if (!isCompleteTime(time)) {
    return undefined;
  }
  return { hour: time.slice(0, TIME_PART_LENGTH), minute: time.slice(TIME_PART_LENGTH + TIME_SEPARATOR.length) };
};

export const withHour = (time: string, hour: string): string =>
  `${hour}${TIME_SEPARATOR}${getTimeParts(time)?.minute ?? START_OF_TIME_PART}`;

export const withMinute = (time: string, minute: string): string =>
  `${getTimeParts(time)?.hour ?? START_OF_TIME_PART}${TIME_SEPARATOR}${minute}`;
